import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Camera } from 'lucide-react';

import { toast } from '../common/Toast/toast';
import { captureScreen } from './captureScreen';
import ScreenCaptureModal from './ScreenCaptureModal.jsx';
import './screen-capture.css';

/* Anything that puts a veil over the page: a dialog says so with
   aria-modal, and the house popups, the export and confirm dialogs, the
   chat's members sheet and the phone row sheet are named as well for the
   few that do not.

   .modal is react-bootstrap's, and it is named for the same reason: its
   Modal renders role="dialog" and NO aria-modal (react-bootstrap's
   Modal.js sets only the role), so seventeen files' dialogs -- the
   trainer dashboard's batch popup and the Add MCQ dialog among them --
   matched nothing here and got no twin, which is exactly the bug the
   twin exists to fix. Its veil sits at 1050-1055 (css/theme/popups.css),
   under the twin's 11000. Safe to name: react-bootstrap unmounts the
   dialog when it is closed, and nothing in this app carries a bare
   `modal` class of its own, so the selector cannot match something that
   is always mounted and leave a camera floating over every page. */
const POPUP_SELECTOR = [
  '[aria-modal="true"]',
  '.pg-modal',
  '.xp',
  '.cnf',
  '.chat-modal-scrim',
  '.td-rowsheet',
  '.modal',
].join(', ');

/**
 * The topbar's screenshot button. Takes the shot first and only then opens
 * the editor, so the editor is never in the picture.
 *
 * `taker` is the signed-in account ({ name, email }) as the topbar already
 * holds it; it is printed under the note so a shared image says who took it.
 *
 * A popup is usually the very thing worth a screenshot, and it covered the
 * button: every popup's veil sits above the topbar (1080+ against the bar's
 * own stacking context), so a click aimed at the camera landed on the veil
 * -- which closes the popup on a direct press. The button could not simply
 * be lifted, because it lives inside the topbar's stacking context and no
 * z-index escapes that. So while a popup is open, a twin of the button is
 * put in <body>, above every veil, and the topbar's own camera is hidden
 * until the popup closes -- one camera on screen, never two.
 *
 * The twin used to be measured onto the real button's spot. On a narrow
 * screen the topbar's buttons are a bar fixed to the BOTTOM of the screen
 * (topbar.css), so the twin landed there, on top of the page's last row.
 * It now has a fixed place of its own, the bottom-right corner, whatever
 * the layout. A press on it never reaches a veil, the popup stays open,
 * and the shot has it in.
 */
const ScreenCapture = ({ taker }) => {
  const [shot, setShot] = useState(null);
  const [capturing, setCapturing] = useState(false);
  // Is a popup open (and the editor not)? Then the camera floats.
  const [floating, setFloating] = useState(false);
  const triggerRef = useRef(null);

  const take = async () => {
    if (capturing) return;
    setCapturing(true);
    try {
      setShot(await captureScreen());
    } catch (error) {
      // Dismissing the browser's share prompt is a choice, not an error.
      if (error?.name !== 'NotAllowedError' && error?.name !== 'AbortError') {
        console.error('Screenshot failed:', error);
        toast.error('Could not take the screenshot. Try again.');
      }
    } finally {
      setCapturing(false);
    }
  };

  /* Is a popup open? Checked whenever the page's DOM changes (a popup
     mounting or unmounting), one check per frame at most. The twin is not
     offered while the screenshot editor itself is open -- a shot taken
     from there would picture the editor. */
  useEffect(() => {
    let frame = 0;

    const check = () => {
      frame = 0;
      setFloating(!shot && Boolean(document.querySelector(POPUP_SELECTOR)));
    };

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };

    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    schedule();

    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [shot]);

  // Not .lms-icon-btn: the topbar's rules size and place that class (a
  // bottom bar on phones), and the twin must answer to none of them.
  const twin =
    floating &&
    typeof document !== 'undefined' &&
    createPortal(
      <button
        type="button"
        className="scap-trigger scap-float"
        onClick={take}
        disabled={capturing}
        aria-label="Take a screenshot"
        title="Screenshot"
      >
        <Camera aria-hidden="true" />
      </button>,
      document.body,
    );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`lms-icon-btn scap-trigger${floating ? ' is-floating' : ''}`}
        // While the twin is out, this one is hidden -- and out of the tab
        // order -- so there is one camera on screen, not two.
        aria-hidden={floating || undefined}
        tabIndex={floating ? -1 : undefined}
        onClick={take}
        disabled={capturing}
        aria-label="Take a screenshot"
        title="Screenshot"
      >
        <Camera aria-hidden="true" />
      </button>
      {twin}
      {shot && <ScreenCaptureModal shot={shot} taker={taker} onClose={() => setShot(null)} />}
    </>
  );
};

export default ScreenCapture;
